/**
 * Domain enumerations shared by the Worker and the web app.
 *
 * Every value here is also a CHECK constraint in the database (Phase 2), so this
 * file and the schema are deliberately kept in lockstep.
 */

// ---------------------------------------------------------------------- roles

/**
 * System-wide role, stored on `users.system_role`.
 *
 * `guest` was deliberately dropped (decision 2026-07-26) — read-only access is
 * expressed per board via {@link BOARD_ROLES}, which avoids a fourth global
 * state to reason about and test.
 */
export const SYSTEM_ROLES = ['super_admin', 'admin', 'member'] as const;
export type SystemRole = (typeof SYSTEM_ROLES)[number];

/** Per-board role, stored on `board_members.role`. Independent of system role. */
export const BOARD_ROLES = ['owner', 'editor', 'viewer'] as const;
export type BoardRole = (typeof BOARD_ROLES)[number];

/** Ordered least → most privileged, for comparison helpers. */
export const SYSTEM_ROLE_RANK: Record<SystemRole, number> = {
  member: 0,
  admin: 1,
  super_admin: 2,
};

export const BOARD_ROLE_RANK: Record<BoardRole, number> = {
  viewer: 0,
  editor: 1,
  owner: 2,
};

export const SYSTEM_ROLE_LABELS: Record<SystemRole, string> = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  member: 'Member',
};

export const BOARD_ROLE_LABELS: Record<BoardRole, string> = {
  owner: 'Owner',
  editor: 'Editor',
  viewer: 'Viewer',
};

// --------------------------------------------------------------- user account

export const USER_STATUSES = ['invited', 'active', 'suspended', 'deactivated'] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

/** Identity providers. `password` is the break-glass path; `google` is primary. */
export const AUTH_PROVIDERS = ['password', 'google'] as const;
export type AuthProvider = (typeof AUTH_PROVIDERS)[number];

/**
 * Password hashing algorithm, stored per user so hashes can be upgraded
 * transparently on next successful login (see docs/00-platform-comparison.md §3).
 */
export const PASSWORD_ALGOS = ['pbkdf2-sha256', 'argon2id'] as const;
export type PasswordAlgo = (typeof PASSWORD_ALGOS)[number];

// -------------------------------------------------------------------- theming

export const THEMES = ['light', 'dark', 'system'] as const;
export type Theme = (typeof THEMES)[number];

// ------------------------------------------------------------------ card data

export const CARD_PRIORITIES = ['none', 'low', 'medium', 'high', 'urgent'] as const;
export type CardPriority = (typeof CARD_PRIORITIES)[number];

export const CARD_PRIORITY_LABELS: Record<CardPriority, string> = {
  none: 'No priority',
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent',
};

/** Board visibility within the single workspace. */
export const BOARD_VISIBILITIES = ['private', 'workspace'] as const;
export type BoardVisibility = (typeof BOARD_VISIBILITIES)[number];

export const ATTACHMENT_KINDS = ['upload', 'drive_link', 'external_link'] as const;
export type AttachmentKind = (typeof ATTACHMENT_KINDS)[number];

// ----------------------------------------------------------------- activities

/**
 * Every mutation emits exactly one activity of one of these types. This list is
 * the vocabulary of both the card activity timeline and the realtime protocol.
 */
export const ACTIVITY_TYPES = [
  'board.created',
  'board.updated',
  'board.archived',
  'board.restored',
  'board.member_added',
  'board.member_removed',
  'board.member_role_changed',
  'list.created',
  'list.renamed',
  'list.moved',
  'list.archived',
  'card.created',
  'card.updated',
  'card.moved',
  'card.archived',
  'card.restored',
  'card.member_added',
  'card.member_removed',
  'card.label_added',
  'card.label_removed',
  'card.due_date_set',
  'card.due_date_cleared',
  'comment.created',
  'comment.updated',
  'comment.deleted',
  'checklist.created',
  'checklist.renamed',
  'checklist.deleted',
  'checklist.item_added',
  'checklist.item_toggled',
  'checklist.item_edited',
  'checklist.item_removed',
  'checklist.item_moved',
  'attachment.added',
  'attachment.removed',
] as const;
export type ActivityType = (typeof ACTIVITY_TYPES)[number];

export const NOTIFICATION_TYPES = [
  'mention',
  'card_assigned',
  'due_soon',
  'overdue',
  'board_invitation',
  'comment_reply',
] as const;
export type NotificationType = (typeof NOTIFICATION_TYPES)[number];
